import type { TaskTableRow } from "@/views/Risk/Tasks/utils";

/**
 * Actions the user has created themselves — today from a Trial News finding
 * ("+" on a finding card) or the Add action modal. They are demo-local state in
 * localStorage, like the hidden-tasks set, and this module is the single source
 * of truth: the nav count pill, the Actions screen and the "+" buttons all read
 * the same snapshot through `useUserActions`, so adding one updates all three.
 *
 * Kept as a module-level store rather than component state because those three
 * consumers live in unrelated parts of the tree.
 */

const STORAGE_KEY = "vivo_user_actions";

/**
 * Seeded RBQM tasks occupy T000001-T000006 and are shown on Quality > Tasks.
 * User actions start well past them so a generated id can never collide with a
 * seeded one — the two share the drawer, whose comments are keyed by task id.
 */
const USER_ACTION_ID_BASE = 100;

const DEFAULT_DUE_IN_DAYS = 14;

/**
 * The shape this module needs from a Trial News finding. Declared here rather
 * than imported from TrialNewsReport so the store does not depend on that
 * component; `InsightFinding` is structurally assignable to it.
 */
export type ActionSourceFinding = {
  id: string;
  kind: "issue" | "signal" | "opportunity";
  title: string;
  description: string;
  recommendation: string;
  severity?: string | null;
};

export type UserAction = {
  /**
   * The table row, carrying exactly the keys of a seeded tasks.json record.
   * Nothing else may live in here: the Tasks/Actions table derives its columns
   * from the keys of the first row, so a stray field becomes a stray column.
   */
  row: TaskTableRow;
  /** Which Trial News finding produced this action, for the "added" check. */
  findingId: string | null;
  trialId: string | null;
  createdAt: string;
};

type Listener = () => void;

const listeners = new Set<Listener>();
let snapshot: UserAction[] = load();

function load(): UserAction[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Rows written by an older shape (or hand-edited storage) are dropped
    // rather than rendered as blank table rows.
    return parsed.filter(
      (entry): entry is UserAction =>
        !!entry &&
        typeof entry === "object" &&
        !!(entry as UserAction).row &&
        typeof (entry as UserAction).row === "object",
    );
  } catch {
    return [];
  }
}

function persist(next: UserAction[]) {
  snapshot = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — the in-memory snapshot still drives this session */
  }
  listeners.forEach((listener) => listener());
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): UserAction[] {
  return snapshot;
}

function nextTaskId(existing: UserAction[]): string {
  const highest = existing.reduce((max, action) => {
    const digits = String(action.row.task_id ?? "").replace(/\D/g, "");
    const value = digits ? Number(digits) : 0;
    return value > max ? value : max;
  }, USER_ACTION_ID_BASE);
  return `T${String(highest + 1).padStart(6, "0")}`;
}

function dueDate(fromDate: Date, inDays = DEFAULT_DUE_IN_DAYS): string {
  const due = new Date(fromDate);
  due.setDate(due.getDate() + inDays);
  return due.toISOString().slice(0, 10);
}

/**
 * A finding's urgency in task terms. Issues carry the agent's severity; a
 * signal is something to look at, an opportunity is optional improvement.
 */
function priorityFor(finding: ActionSourceFinding): string {
  if (finding.kind === "opportunity") return "Low";
  if (finding.kind === "signal") return "Medium";
  const severity = finding.severity?.toLowerCase();
  switch (severity) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "low":
      return "Low";
    default:
      return "Medium";
  }
}

export type BuildActionOptions = {
  finding: ActionSourceFinding;
  /** Label of the agent whose report raised it, e.g. "Safety Monitoring". */
  roleLabel?: string | null;
  assignedTo?: string | null;
  trialId?: string | null;
  now?: Date;
};

export function buildActionFromFinding({
  finding,
  roleLabel,
  assignedTo,
  trialId,
  now = new Date(),
}: BuildActionOptions): UserAction {
  const recommendation = finding.recommendation?.trim();
  return {
    row: {
      task_id: nextTaskId(snapshot),
      task_title: finding.title,
      // A Trial News finding has no RBQM breach or risk record behind it; the
      // table and drawer render a missing value as "-".
      issue_id: null,
      // The Signal column is where this action's provenance shows on the table.
      signal: roleLabel ? `Trial News · ${roleLabel}` : "Trial News",
      risk: null,
      description: finding.description,
      assigned_role: assignedTo?.trim() || null,
      priority: priorityFor(finding),
      due_date: dueDate(now),
      status: "Open",
      comments: recommendation ? `Recommendation: ${recommendation}` : null,
    },
    findingId: finding.id,
    trialId: trialId ?? null,
    createdAt: now.toISOString(),
  };
}

/**
 * Adds an action for a finding. Adding the same finding twice is a no-op, so a
 * double click cannot produce duplicate rows.
 */
export function addActionFromFinding(options: BuildActionOptions): UserAction {
  const existing = snapshot.find(
    (action) => action.findingId === options.finding.id,
  );
  if (existing) return existing;

  const action = buildActionFromFinding(options);
  persist([...snapshot, action]);
  return action;
}

export type ManualActionInput = {
  title: string;
  description?: string | null;
  priority?: string | null;
  assignedTo?: string | null;
  dueDate?: string | null;
  linkedRisk?: string | null;
  status?: string;
  now?: Date;
};

/** Adds an action the user typed into the Add action modal. */
export function addManualAction({
  title,
  description,
  priority,
  assignedTo,
  dueDate: due,
  linkedRisk,
  status = "Open",
  now = new Date(),
}: ManualActionInput): UserAction {
  const action: UserAction = {
    row: {
      task_id: nextTaskId(snapshot),
      task_title: title,
      issue_id: null,
      signal: null,
      risk: linkedRisk?.trim() || null,
      description: description?.trim() || null,
      assigned_role: assignedTo?.trim() || null,
      priority: priority?.trim() || "Medium",
      due_date: due?.trim() || dueDate(now),
      status,
      comments: null,
    },
    findingId: null,
    trialId: null,
    createdAt: now.toISOString(),
  };
  persist([...snapshot, action]);
  return action;
}

export function removeAction(taskId: string) {
  persist(snapshot.filter((action) => String(action.row.task_id) !== taskId));
}

export function clearActions() {
  persist([]);
}
